import { useEffect, useState, useRef, useCallback } from 'react'
import { UNSPLASH_API } from '../services'

/**
 * Custom hook to fetch Unsplash images on-demand
 * Includes caching to prevent duplicate API calls
 * Uses requestIdleCallback to defer non-critical fetches
 * 
 * @param {Object} options - Configuration options
 * @param {string} options.query - Search query (default: 'nature')
 * @param {number} options.perPage - Number of images to fetch (default: 10)
 * @param {string} options.orientation - Image orientation: 'landscape' | 'portrait' | 'squarish' (default: 'landscape')
 * @param {boolean} options.enabled - Whether to fetch images (default: true)
 * @param {boolean} options.defer - Whether to defer fetch until browser is idle (default: true)
 * @returns {Object} { images, loading, error, refetch }
 */
export function useUnsplashImages({
  query = 'nature',
  perPage = 10,
  orientation = 'landscape',
  enabled = true,
  defer = true,
} = {}) {
  const [images, setImages] = useState({
    results: [],
    total: 0,
    total_pages: 0,
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  
  // Track if we've fetched for current parameters
  const currentCacheKey = `${query}-${perPage}-${orientation}`
  const lastCacheKey = useRef('')
  const hasFetched = useRef(false)

  const fetchImages = useCallback(async () => {
    // Prevent duplicate calls for same parameters
    if (hasFetched.current && lastCacheKey.current === currentCacheKey) {
      return
    }

    setLoading(true)
    setError(null)
    hasFetched.current = true
    lastCacheKey.current = currentCacheKey

    try {
      const imageResponses = await UNSPLASH_API.search.getPhotos({
        page: 1,
        perPage,
        orderBy: 'popular',
        contentFilter: 'high',
        orientation,
        query,
      })

      if (imageResponses?.status === 200) {
        setImages(imageResponses?.response || {
          results: [],
          total: 0,
          total_pages: 0,
        })
      } else {
        setError(new Error('Failed to fetch images from Unsplash'))
      }
    } catch (err) {
      console.error('[useUnsplashImages] Error fetching images:', err)
      setError(err)
    } finally {
      setLoading(false)
    }
  }, [query, perPage, orientation, currentCacheKey])

  const refetch = useCallback(() => {
    hasFetched.current = false
    lastCacheKey.current = ''
    fetchImages()
  }, [fetchImages])

  useEffect(() => {
    if (!enabled) {
      return
    }

    // Defer fetch until browser is idle for better performance
    if (defer) {
      if ('requestIdleCallback' in window) {
        const idleCallback = requestIdleCallback(
          fetchImages,
          { timeout: 2000 } // Max wait 2 seconds
        )
        return () => {
          if (typeof idleCallback === 'number') {
            cancelIdleCallback(idleCallback)
          }
        }
      } else {
        // Fallback for browsers without requestIdleCallback
        const timer = setTimeout(fetchImages, 100)
        return () => clearTimeout(timer)
      }
    } else {
      // Fetch immediately
      fetchImages()
    }
  }, [enabled, defer, fetchImages]) // Include fetchImages in dependencies

  return {
    images,
    loading,
    error,
    refetch,
  }
}

